import { useContext, useEffect, useMemo, useState } from 'react';
import isEqual from 'lodash/isEqual';

import { ApplicationContext, ConfigurationsContext } from '../../App';
import { Application } from 'libapi';
import { Bay, MachineConfig, PalletConfigObj } from 'blackbox';
import { getConfigValueFromKey, useMachineConfig } from '../General';
import Button from '../PendantButton';
import { AnimatedToggle, ComboBox, ConfirmationModal } from '../Settings';
import { COLOR } from '../shared/shared';

interface BayFormProps {
	updateMachineConfig: () => void;
}

function getAllSSDisabled(bays: Bay[]): boolean {
	return bays.every(bay => bay.SlipSheetMags.every(m => !m));
}

function toggleSlipSheet(mags: boolean[], index: number): boolean[] {
	const newMags = [...mags];
	newMags[index] = !newMags[index];

	if (!newMags[index]) {
		for (let i = index + 1; i < newMags.length; i++) newMags[i] = false;
	} else {
		for (let i = 0; i < index; i++) newMags[i] = true;
	}

	return newMags;
}

export default function BayForm({ updateMachineConfig }: BayFormProps) {
	const app = useContext(ApplicationContext) as Application;
	const { reload_cfs } = useContext(ConfigurationsContext) as any;

	const [localBays, setLocalBays] = useState<Bay[]>([]);
	const [originalConfig, setOriginalConfig] = useState<MachineConfig>();
	const [badFormatString, setBadFormatString] = useState('');
	const [showConfirmSave, setShowConfirmSave] = useState(false);
	const [asyncMode, setAsyncMode] = useState(false);

	const lConveyorValues = ['Conveyor 1', 'Conveyor 2'];
	const lMachineConfig = useMachineConfig(app).lMachineConfig;

	useEffect(() => {
		async function loadInitialData() {
			if (lMachineConfig?.Bays) {
				const orig = JSON.parse(JSON.stringify(lMachineConfig)) as MachineConfig;
				setOriginalConfig(orig);
				setLocalBays(JSON.parse(JSON.stringify(lMachineConfig.Bays)));
			}
			try {
				const ac = await app.api.CoreAppConfig.get();
				const asyncModeValue = getConfigValueFromKey('ASYNC', ac);
				setAsyncMode(Boolean(asyncModeValue));
			} catch (error) {
				console.error('Error loading async mode:', error);
			}
		}

		loadInitialData();
	}, [lMachineConfig]);

	const madeChanges = useMemo(() => {
		return !!originalConfig && !isEqual(localBays, originalConfig.Bays);
	}, [localBays, originalConfig]);

	const asyncConflict =
		asyncMode &&
		localBays.length >= 2 &&
		localBays[0].InfeedIdx === localBays[1].InfeedIdx;

	const handleToggleBay = (index: number) => {
		setLocalBays(bs =>
			bs.map((bay, i) => (i === index ? { ...bay, Enabled: !bay.Enabled } : bay))
		);
		setBadFormatString('');
	};

	const handleToggleSlipSheet = (bayIndex: number, magIndex: number) => {
		setLocalBays(bs =>
			bs.map((bay, i) =>
				i === bayIndex
					? {
							...bay,
							SlipSheetMags: toggleSlipSheet(bay.SlipSheetMags, magIndex),
					  }
					: bay
			)
		);
		setBadFormatString('');
	};

	const handleChangeInfeedIdx = (index: number, value: string) => {
		setLocalBays(bs =>
			bs.map((bay, i) =>
				i === index ? { ...bay, InfeedIdx: lConveyorValues.indexOf(value) } : bay
			)
		);
		setBadFormatString('');
	};

	async function handleSave() {
		if (!originalConfig) return;
		setShowConfirmSave(false);

		const numEnabledBays = localBays.filter(b => b.Enabled).length;
		if (numEnabledBays === 0) {
			setBadFormatString('At least one pallet must be enabled.');
			return;
		}

		if (numEnabledBays === 2 && asyncConflict) {
			setBadFormatString(
				'Disable Asynchronous mode in settings to use the same conveyor for both pallets.'
			);
			return;
		}

		if (getAllSSDisabled(localBays)) {
			try {
				const pPalletConfig: PalletConfigObj = await app.api.Pallet.get();
				pPalletConfig.config.pallets.forEach(pallet => {
					pallet.Stack.forEach(stack => {
						stack.slipSheet = {
							config: 0,
							exclude: {},
							ids: {},
						};
					});
				});
				await app.api.Pallet.update(pPalletConfig);
				console.log('Successfully removed slipsheets from stacks.');
				reload_cfs();
			} catch (e) {
				console.error('Error removing slipsheets from stacks.', e);
			}
		}

		const lNewConfig = { ...originalConfig, Bays: localBays };
		await app.api.Machine.update(lNewConfig);

		setBadFormatString('');
		updateMachineConfig();
		app.restartCore();
		app.restartEngine();
	}

	return (
		<>
			<div className="full-size flex-col">
				<div className="flex-row flex-between full-width align-center margin-bottom">
					<span className="font-5 color-red">{badFormatString}</span>
					<Button
						pAction={() => setShowConfirmSave(true)}
						pNormalColorOverride={
							madeChanges && !asyncConflict ? COLOR.LIGHT_BLUE : COLOR.DARK_GREY
						}
						pDisabled={!madeChanges || asyncConflict}
					>
						<span className="vw-8 bold-font">Save</span>
					</Button>
				</div>

				{localBays.map((bay, index) => {
					const lDisabledClass = bay.Enabled ? '' : 'disabled-style';
					return (
						<div
							key={index}
							className="flex-col background-silver rounded double-margin-bottom border padding"
						>
							<div className="flex-row align-center">
								<span className="color-blue font-5 bold-font">{`${bay.Name} Pallet`}</span>
								<div className="double-padding"></div>
								<AnimatedToggle
									pChecked={bay.Enabled}
									pCheckChanged={() => handleToggleBay(index)}
								/>
							</div>
							<div className="padding"></div>
							<div className="height-1vh border-bottom full-width"></div>
							<div className="padding"></div>

							<div className={'flex-col ' + lDisabledClass}>
								<span className="font-5 color-blue bold-font margin-bottom">
									Conveyor for Pallet
								</span>
								<div className="flex-col">
									<ComboBox
										values={lConveyorValues}
										currentValue={lConveyorValues[bay.InfeedIdx]}
										onChange={pNewValue =>
											handleChangeInfeedIdx(index, String(pNewValue))
										}
									/>
								</div>
							</div>
							<div className="double-padding"></div>

							{bay.SlipSheetMags.map((mag, magIndex) => (
								<div
									key={`${magIndex}-${mag}`}
									className={
										'flex-row align-center double-margin-bottom ' + lDisabledClass
									}
								>
									<span className="font-5 color-blue bold-font double-margin-right">
										Slip Sheet Magazine {magIndex + 1}
									</span>
									<AnimatedToggle
										pChecked={mag}
										pCheckChanged={() => handleToggleSlipSheet(index, magIndex)}
									/>
								</div>
							))}
						</div>
					);
				})}
			</div>

			{showConfirmSave && (
				<ConfirmationModal
					t1={`This will reconnect to the robot.${
						getAllSSDisabled(localBays)
							? ' Slip sheets will be removed from all stacks.'
							: ''
					}`}
					t2={'Are you sure?'}
					bCancel={'Cancel'}
					bConfirm={'Save'}
					onConfirm={handleSave}
					onCancel={() => setShowConfirmSave(false)}
				/>
			)}
		</>
	);
}
